package org.moxultimate.topwithmox.dashboard

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.statics.DashboardStatics.IMG_BG_WALLPAPER_SECTION
import org.moxultimate.topwithmox.statics.DashboardStatics.IMG_CURVED_WOOD_BOTTOM
import org.moxultimate.topwithmox.statics.DashboardStatics.IMG_CURVED_WOOD_TOP
import org.moxultimate.topwithmox.statics.DashboardStatics.IMG_WOOD_CURVED

/**
 * SectionFrames
 * Dashboard background frames shared by the Tech and Shows sections, so the background of each
 * dashboard slot stays fixed while the section content placed in it can be reordered.
 */

/**
 * WoodSectionFrameDesktop
 * Wood-textured desktop background with the curved wood top and bottom images.
 *
 * @param content Section content placed inside the centered, width-clamped column.
 */
@Composable
fun WoodSectionFrameDesktop(content: @Composable ColumnScope.() -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("background-image", "url('$IMG_CURVED_WOOD_TOP')")
                property("background-size", "cover")
                property("background-position", "center")
                property("background-repeat", "no-repeat")
            },
        contentAlignment = Alignment.Center,
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            Image(
                src = IMG_CURVED_WOOD_TOP,
                modifier = Modifier.fillMaxWidth().display(DisplayStyle.Block).attrsModifier { attr("loading", "lazy") },
            )
            Image(
                src = IMG_CURVED_WOOD_BOTTOM,
                modifier = Modifier.fillMaxWidth().display(DisplayStyle.Block).attrsModifier { attr("loading", "lazy") },
            )
        }

        Column(
            modifier = Modifier
                .width(clamp(min = 1100.px, value = 80.vw, max = 2000.px))
                .padding(topBottom = 80.px),
            content = content,
        )
    }
}

/**
 * StarfieldSectionFrameDesktop
 * Dark starfield desktop background with an optional curved wood strip along the bottom.
 *
 * @param showCurvedBottom Draws the curved wood strip at the bottom edge when true.
 * @param content Section content placed inside the centered, width-clamped column.
 */
@Composable
fun StarfieldSectionFrameDesktop(
    showCurvedBottom: Boolean,
    content: @Composable ColumnScope.() -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("position", "relative")
                property("background-image", "url('$IMG_BG_WALLPAPER_SECTION')")
                property("background-size", "cover")
                property("background-position", "center")
                property("background-repeat", "no-repeat")
            },
        contentAlignment = Alignment.TopCenter,
    ) {
        if (showCurvedBottom) {
            Image(
                src = IMG_WOOD_CURVED,
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("position", "absolute")
                        property("bottom", "0")
                        property("left", "0")
                        property("z-index", "0")
                        property("object-fit", "cover")
                        property("display", "block")
                    }
                    .attrsModifier { attr("loading", "lazy") },
            )
        }

        Column(
            modifier = Modifier
                .width(clamp(min = 1100.px, value = 80.vw, max = 2000.px))
                .padding(top = 80.px, bottom = 160.px)
                .styleModifier { property("z-index", "1") },
            content = content,
        )
    }
}

/**
 * WoodSectionFrameMobile
 * Wood-textured mobile background; the curved wood images fill the frame behind the content.
 *
 * @param content Section content placed above the background images.
 */
@Composable
fun WoodSectionFrameMobile(content: @Composable ColumnScope.() -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("position", "relative")
                property("background-image", "url('$IMG_CURVED_WOOD_TOP')")
                property("background-size", "cover")
                property("background-position", "center")
                property("background-repeat", "no-repeat")
            },
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("height", "100%")
                    property("z-index", "0")
                },
        ) {
            Image(
                src = IMG_CURVED_WOOD_TOP,
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("flex", "1")
                        property("object-fit", "cover")
                        property("display", "block")
                        property("min-height", "0")
                    }
                    .attrsModifier { attr("loading", "lazy") },
            )
            Image(
                src = IMG_CURVED_WOOD_BOTTOM,
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("flex", "1")
                        property("object-fit", "cover")
                        property("display", "block")
                        property("min-height", "0")
                    }
                    .attrsModifier { attr("loading", "lazy") },
            )
        }

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier { property("z-index", "1") }
                .padding(top = 110.px, bottom = 100.px),
            content = content,
        )
    }
}

/**
 * StarfieldSectionFrameMobile
 * Dark starfield mobile background.
 *
 * @param content Section content placed inside the padded column.
 */
@Composable
fun StarfieldSectionFrameMobile(content: @Composable ColumnScope.() -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("position", "relative")
                property("background-image", "url('$IMG_BG_WALLPAPER_SECTION')")
                property("background-size", "cover")
                property("background-position", "center")
                property("background-repeat", "no-repeat")
            },
        contentAlignment = Alignment.TopCenter,
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(top = 60.px, bottom = 40.px),
            content = content,
        )
    }
}
